// @flow
import React, { useMemo, useRef, useState } from "react";
import _ from "lodash";
import type { Companion, Game, UserData, Location } from "../../common/types";
import {
  useAnalytics,
  useAuth,
  useFirestore,
  useFirestoreCollectionData,
  useFirestoreDocData,
  useFunctions,
} from "reactfire";
import { Field, Form, Formik } from "formik";
import { Button } from "@material-ui/core";
import PageSubHeading from "../../components/PageSubHeading";
import Box from "@material-ui/core/Box";
import * as Yup from "yup";
import { TextField } from "formik-material-ui";
import CardContent from "@material-ui/core/CardContent";
import Card from "@material-ui/core/Card";
import EditRoundedIcon from "@material-ui/icons/EditRounded";
import Typography from "@material-ui/core/Typography";
import Chip from "@material-ui/core/Chip";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { useSnackbar } from "notistack";
import TicketReservationAddCompanionModal from "./TicketReservationAddCompanionModal";
import TicketReservationFooter from "./TicketReservationFooter";
import TicketReservationHints from "./TicketReservationHints";
import { useHistory } from "react-router-dom";
import TicketReservationSoldOut from "./TicketReservationSoldOut";
import ticketReservationSelectors from "../../selectors/ticketReservationSelectors";
import LocationListItem from "../../components/LocationListItem";
import List from "@material-ui/core/List";
import CardActionArea from "@material-ui/core/CardActionArea";
import { lighten, darken } from "@material-ui/core/styles/colorManipulator";
import Radio from "@material-ui/core/Radio";
import Grid from "@material-ui/core/Grid";
import { loadStripe } from "@stripe/stripe-js/pure";

type Props = {
  game: Game,
  location: Location,
};

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const ticketReservationSchema = Yup.object().shape({
  name: Yup.string().min(2, "Benötigte Angabe").required("Benötigte Angabe"),
  phoneNumber: Yup.string()
    .min(5, "Benötigte Angabe")
    .max(15, "Zu lange Nummer")
    .required("Benötigte Angabe"),
  streetAddress: Yup.string()
    .min(2, "Benötigte Angabe")
    .required("Benötigte Angabe"),
  postalCode: Yup.string()
    .matches(/^[0-9]{5}(?:-[0-9]{4})?$/, "Benötigte Angabe")
    .required("Benötigte Angabe"),
  paymentMethod: Yup.string().required("Bitte wähle eine Zahlungsmethode"),
});

const getUserDataFields = (userData: UserData, user) => [
  {
    name: "name",
    type: "text",
    label: "Name",
    autoComplete: "name",
    validation: Yup.string()
      .min(2, "Benötigte Angabe")
      .required("Benötigte Angabe"),
    disabled: Boolean(userData.name),
  },
  {
    name: "phoneNumber",
    type: "tel",
    label: "Mobilfunknummer",
    autoComplete: "tel",
    disabled: Boolean(user.phoneNumber),
  },
  {
    name: "streetAddress",
    type: "text",
    label: "Straße. Nr.",
    autoComplete: "street-address",
  },
  {
    name: "postalCode",
    type: "tel",
    label: "Postleitzahl",
    autoComplete: "postal-code",
  },
];

/**
 * Page plan:
 *
 * 1. get user personal data if not present
 * 2. show user data if present
 *
 * 3. ask for other guests
 * 4. suggest guests, that have been there
 *
 * 5. show price and ticket calculation
 * 6. let user agree to terms
 *
 * 7. start buying tickets
 *    - save user information on user
 *    - save his friends in array
 *    - POST function to buy tickets with IDs of user and his friends
 *
 * 8. on success redirect the user to his ticket
 * 9. on error fail gracefully
 *
 */

function TicketReservationForm({ game, location }: Props) {
  const firestore = useFirestore();
  const auth = useAuth();
  const functions = useFunctions();
  const analytics = useAnalytics();
  const history = useHistory();
  const paymentMethodRef = useRef(null);

  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const [ticketCount, setTicketCount] = useState(1);
  const [companionIds, setCompanionIds] = useState([]);
  const [forceEditUserData, setForceEditUserData] = useState(false);

  const user = auth.currentUser;
  const userDataRef = firestore.collection("users").doc(user.uid);
  const userData: UserData = useFirestoreDocData(userDataRef);

  const companionsRef = userDataRef.collection("companions");
  const companions: Array<Companion> = useFirestoreCollectionData(
    companionsRef
  );

  const companionsFiltered = useMemo(
    () =>
      _.orderBy(
        _.filter(companions, (c) => !c.hiddenByUser),
        "name",
        "asc"
      ),
    [companions]
  );

  const trs = ticketReservationSelectors.getTicketReservationStatus(game);

  if (trs !== "OPEN") {
    return trs === "CLOSED"
      ? "Dieses Spiel ist bereits beendet."
      : trs === "SCHEDULED"
      ? "Die Reservierung ist noch nicht möglich. Sie beginnt 8 Tage vor Spielbeginn."
      : "Für dieses Spiel wird keine Reservierung angeboten.";
  }

  const userComplete =
    userData.name != null &&
    userData.phoneNumber != null &&
    userData.streetAddress != null &&
    userData.postalCode != null;

  const addCompanion = async (companionId: string) => {
    if (ticketCount >= 5) {
      enqueueSnackbar("Du kannst maximal 5 Tickets reservieren", {
        variant: "info",
      });
      return;
    }
    const newCompanionIds = [...companionIds, companionId];
    await setCompanionIds(newCompanionIds);
    setTicketCount(ticketCount + 1);
  };

  const removeCompanion = async (companionId: string) => {
    const newCompanionIds = companionIds.filter((c) => c !== companionId);
    await setCompanionIds(newCompanionIds);
    setTicketCount(ticketCount - 1);
  };

  const userDataFields = getUserDataFields(userData, user);

  const availableSlots =
    (game.visitorSlots || location.visitorSlots) - game.ticketReservationCount;

  if (availableSlots <= 0) {
    return <TicketReservationSoldOut />;
  }

  const scrollToPaymentMethod = () =>
    window.scrollTo({
      top: paymentMethodRef.current.offsetTop - 100,
      behavior: "smooth",
    });

  return (
    <Formik
      validationSchema={ticketReservationSchema}
      initialValues={{
        name: userData.name || user.displayName || "",
        phoneNumber: userData.phoneNumber || user.phoneNumber || "",
        streetAddress: userData.streetAddress || "",
        postalCode: userData.postalCode || "",
        paymentMethod: "",
      }}
      onSubmit={async (values) => {
        const data = { ...values, gameId: game.id, companionIds };

        // save userData if its wasn't present
        if (
          !Boolean(userData.name) ||
          !Boolean(userData.phoneNumber) ||
          !Boolean(userData.streetAddress) ||
          !Boolean(userData.postalCode)
        ) {
          userDataRef
            .set(
              {
                name: values.name,
                phoneNumber: values.phoneNumber,
                streetAddress: values.streetAddress,
                postalCode: values.postalCode,
              },
              { merge: true }
            )
            .catch((error) => console.error(error));
        }

        const addTicketReservation = functions.httpsCallable(
          "addTicketReservation"
        );
        await addTicketReservation(data)
          .then(async (result) => {
            analytics.logEvent("purchase", {
              value: ticketCount * 3,
              currency: "EUR",
            });
            const { sessionId } = result.data;

            /**
             * init payment if exists, else redirect
             */
            if (sessionId != null) {
              const stripe = await stripePromise;
              // When the customer clicks on the button, redirect them to Checkout.
              const result = await stripe.redirectToCheckout({
                sessionId: sessionId,
              });

              if (result.error) {
                // If `redirectToCheckout` fails due to a browser or network
                // error, display the localized error message to your customer
                // using `result.error.message`.
                console.error(result.error);
                enqueueSnackbar(result.error.message, { variant: "error" });
              }
              console.warn("Runned fully, maybe with unsuccessfull error");
            }

            return history.push(`/ticket/${game.id}?newTicket=true`);
          })
          .catch((error) => {
            enqueueSnackbar(error.message, { variant: "error" });
            console.error(error);
          });
      }}
    >
      {({
        submitForm,
        isSubmitting,
        values,
        setFieldValue,
        setFieldTouched,
        touched,
        isValid,
      }) => (
        <Form style={{ width: "100%" }}>
          <PageSubHeading
            primary="Deine Angaben"
            secondary="Diese werden ausschließlich zur Nachverfolgung von Corona-Infektionsketten verwendet."
          />
          {userComplete && !forceEditUserData ? (
            <Card variant="outlined">
              <CardContent>
                <Button
                  variant="text"
                  color="secondary"
                  endIcon={<EditRoundedIcon />}
                  onClick={() => setForceEditUserData(true)}
                  style={{ float: "right" }}
                >
                  Ändern
                </Button>
                {user.photoURL != null && (
                  <img
                    src={user.photoURL}
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 40,
                      objectFill: "cover",
                      marginRight: 10,
                      float: "left",
                      marginBottom: 30,
                    }}
                    alt={`Bild von ${userData.name}`}
                  />
                )}
                <Typography>
                  {userData.name || "asd"}
                  <br />
                  {userData.phoneNumber || "asd"}
                </Typography>
                <Typography color="textSecondary" variant="body2">
                  {userData.streetAddress || "asd"},{" "}
                  {userData.postalCode || "asd"}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            userDataFields.map(({ name, type, label, ...other }) => (
              <Box mb={1} key={name}>
                <Field
                  component={TextField}
                  name={name}
                  type={type}
                  label={label}
                  variant="filled"
                  {...other}
                />
              </Box>
            ))
          )}
          <PageSubHeading
            primary="Begleiter:innen"
            secondary="Du kannst bis zu vier weitere Tickets hinzufügen."
          />
          <Box mb={3} className={classes.chips}>
            {companionsFiltered.map((companion) => {
              const selected = companionIds.includes(companion.id);
              return (
                <Chip
                  key={companion.id}
                  label={companion.name}
                  clickable
                  onClick={
                    selected ? undefined : () => addCompanion(companion.id)
                  }
                  onDelete={
                    selected ? () => removeCompanion(companion.id) : undefined
                  }
                  variant={selected ? "default" : "outlined"}
                  color="primary"
                />
              );
            })}
            <TicketReservationAddCompanionModal
              handleSubmit={(companionId) => addCompanion(companionId)}
            />
          </Box>
          <PageSubHeading
            primary="Zahlung"
            secondary="Bitte wähle eine Zahlungsmethode"
            forwardRef={paymentMethodRef}
            classes={
              touched.paymentMethod && !isValid
                ? { secondary: classes.errorLabel }
                : {}
            }
          />
          <Card
            variant="outlined"
            classes={
              values.paymentMethod === "online"
                ? {
                    root: classes.selected,
                  }
                : {}
            }
          >
            <CardActionArea
              onClick={() => setFieldValue("paymentMethod", "online")}
            >
              <CardContent>
                <Grid container spacing={1} wrap="nowrap">
                  <Grid item>
                    <Radio
                      checked={values.paymentMethod === "online"}
                      onChange={() => setFieldValue("paymentMethod", "online")}
                      color="primary"
                    />
                  </Grid>
                  <Grid item container>
                    <Grid container alignItems="center" spacing={1}>
                      <Grid item>
                        <Typography>Online</Typography>
                      </Grid>
                      <Grid item>
                        <Typography
                          className={classes.label}
                          variant="overline"
                          component="div"
                        >
                          Empfohlen
                        </Typography>
                      </Grid>
                    </Grid>
                    <Typography color="textSecondary" variant="body2">
                      Jetzt kontaktlos und sicher per Kreditkarte zahlen
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </CardActionArea>
          </Card>
          <Box mt={1} />
          <Card
            variant="outlined"
            classes={
              values.paymentMethod === "offline"
                ? {
                    root: classes.selected,
                  }
                : {}
            }
          >
            <CardActionArea
              onClick={() => setFieldValue("paymentMethod", "offline")}
            >
              <CardContent>
                <Grid container spacing={1} wrap="nowrap">
                  <Grid item>
                    <Radio
                      checked={values.paymentMethod === "offline"}
                      onChange={() => setFieldValue("paymentMethod", "offline")}
                      color="primary"
                    />
                  </Grid>
                  <Grid item>
                    <Typography>Abendkasse</Typography>
                    <Typography color="textSecondary" variant="body2">
                      Später in Bar zahlen – die Reservierung verfällt 15
                      Minuten vor Spielbeginn
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </CardActionArea>
          </Card>

          <PageSubHeading primary="Hinweise" />
          <TicketReservationHints />
          <PageSubHeading primary="Spielort" />
          <List>
            <LocationListItem gameId={game.id} />
          </List>
          <TicketReservationFooter
            handleSubmit={submitForm}
            isSubmitting={isSubmitting}
            ticketCount={ticketCount}
            isValid={Boolean(values.paymentMethod)}
            scrollToError={() => {
              setFieldTouched("paymentMethod");
              scrollToPaymentMethod();
            }}
          />
        </Form>
      )}
    </Formik>
  );
}
const useStyles = makeStyles((theme: Theme) => {
  const getColor = theme.palette.type === "light" ? darken : lighten;
  const getBackgroundColor = theme.palette.type === "light" ? lighten : darken;

  const color = "primary";

  return createStyles({
    chips: {
      "& > *": {
        marginRight: theme.spacing(1),
        marginBottom: theme.spacing(1),
      },
    },
    selected: {
      borderColor: theme.palette[color].main,
      color: getColor(theme.palette[color].main, 0.6),
      backgroundColor: getBackgroundColor(theme.palette[color].main, 0.9),
      borderWidth: 1,
    },
    label: {
      backgroundColor: theme.palette[color].main,
      color: "#fff",
      borderRadius: 6,
      paddingLeft: 6,
      paddingRight: 6,
      paddingTop: 4,
      paddingBottom: 4,
      lineHeight: 1,
    },
    errorLabel: {
      color: "red",
    },
  });
});

export default TicketReservationForm;
